import { useEffect, useRef, useCallback, useState, createContext, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { logger } from '@/lib/logger';

interface BackgroundSyncOptions {
  /** Minimum time (ms) tab must be hidden before triggering sync on return. Default: 30s */
  minHiddenTime?: number;
  /** Interval (ms) for periodic background sync while hidden. Set to 0 to disable. Default: 0 */
  backgroundInterval?: number;
  /** Query keys to sync. If empty, syncs all stale active queries. */
  queryKeys?: string[][];
  /** Enable debug logging */
  debug?: boolean;
  /** Callback when sync state changes */
  onSyncStateChange?: (isSyncing: boolean) => void;
}

interface BackgroundSyncContextValue {
  isSyncing: boolean;
  setIsSyncing: (value: boolean) => void;
}

// Context for sharing sync state across components
export const BackgroundSyncContext = createContext<BackgroundSyncContextValue>({
  isSyncing: false,
  setIsSyncing: () => {},
});

export const useBackgroundSyncState = () => useContext(BackgroundSyncContext);

/**
 * Hook for background sync - refetches stale data when user returns to tab.
 * 
 * Features:
 * - Syncs data when tab becomes visible after being hidden
 * - Optional periodic background sync while tab is hidden
 * - Only refetches stale queries (respects staleTime)
 * - Configurable minimum hidden time before sync
 * 
 * Usage:
 * ```tsx
 * // Basic - sync all stale queries when returning to tab
 * useBackgroundSync();
 * 
 * // With specific queries
 * useBackgroundSync({
 *   queryKeys: [['chefStats'], ['livraisons'], ['notifications']],
 *   minHiddenTime: 60000, // 1 minute
 * });
 * ```
 */
export function useBackgroundSync(options: BackgroundSyncOptions = {}) {
  const {
    minHiddenTime = 30 * 1000, // 30 seconds
    backgroundInterval = 0, // Disabled by default
    queryKeys = [],
    debug = false,
    onSyncStateChange,
  } = options;

  const queryClient = useQueryClient();
  const hiddenSinceRef = useRef<number | null>(null);
  const backgroundIntervalRef = useRef<number | null>(null);

  const log = useCallback((...args: any[]) => {
    if (debug) {
    }
  }, [debug]);

  const syncQueries = useCallback(async () => {
    log('🔄 Syncing stale queries...');
    onSyncStateChange?.(true);
    
    try {
      if (queryKeys.length > 0) {
        // Sync specific queries
        await Promise.all(
          queryKeys.map(key => 
            queryClient.invalidateQueries({ 
              queryKey: key,
              refetchType: 'active', // Only refetch if component is mounted
            })
          )
        );
        log('✅ Synced', queryKeys.length, 'query groups');
      } else {
        // Sync all stale active queries
        await queryClient.refetchQueries({
          stale: true,
          type: 'active',
        });
        log('✅ Synced all stale active queries');
      }
    } catch (error) {
      logger.error("useBackgroundSync", "Error syncing queries:", error);
    } finally {
      // Small delay to show spinner
      setTimeout(() => {
        onSyncStateChange?.(false);
      }, 500);
    }
  }, [queryClient, queryKeys, log, onSyncStateChange]);

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // Tab became hidden - record timestamp
      hiddenSinceRef.current = Date.now();
      log('👁️ Tab hidden at', new Date().toLocaleTimeString());
      
      // Start background interval if configured
      if (backgroundInterval > 0 && !backgroundIntervalRef.current) {
        backgroundIntervalRef.current = window.setInterval(() => {
          log('⏰ Background interval sync');
          syncQueries();
        }, backgroundInterval);
      }
    } else {
      // Tab became visible - check if we should sync
      const hiddenDuration = hiddenSinceRef.current 
        ? Date.now() - hiddenSinceRef.current 
        : 0;
      
      log('👁️ Tab visible after', Math.round(hiddenDuration / 1000), 'seconds');
      
      // Clear background interval
      if (backgroundIntervalRef.current) {
        clearInterval(backgroundIntervalRef.current);
        backgroundIntervalRef.current = null;
      }
      
      // Sync if hidden long enough
      if (hiddenDuration >= minHiddenTime) {
        log('📡 Triggering sync (hidden >', minHiddenTime / 1000, 's)');
        syncQueries();
      }
      
      hiddenSinceRef.current = null;
    }
  }, [minHiddenTime, backgroundInterval, syncQueries, log]);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Cleanup interval on unmount
      if (backgroundIntervalRef.current) {
        clearInterval(backgroundIntervalRef.current);
      }
    };
  }, [handleVisibilityChange]);

  // Return manual sync function for programmatic use
  return { syncQueries };
}

/**
 * Global background sync - use in App.tsx for app-wide sync.
 * Syncs critical queries when user returns after 30+ seconds.
 */
export function useGlobalBackgroundSync(onSyncStateChange?: (isSyncing: boolean) => void) {
  return useBackgroundSync({
    minHiddenTime: 30 * 1000, // 30 seconds
    queryKeys: [
      ['chefStats'],
      ['livraisons'],
      ['notifications', 'unread-count'],
      ['concrete-pour-detail'],
      ['siteBetonnages'],
      ['sitePlanning'],
    ],
    debug: process.env.NODE_ENV === 'development',
    onSyncStateChange,
  });
}
